import type { TrackerCopy } from './types'

/** Tracker content in English. */
export const TRACKER_COPY_EN: TrackerCopy = {

  meta: {
    title: 'Where Is Santa? Christmas Countdown and Santa Tracker',
    description:
      "See how long until Christmas, follow Santa's journey around the world and discover when he may arrive in your time zone.",
    keywords: [
      'where is santa',
      'santa tracker',
      'christmas countdown',
      'how long until christmas',
      'how many days until christmas',
      'what time does santa come',
      'santa route',
      'track santa',
      'where is santa claus now',
      'santa claus map',
      'christmas eve tracker',
    ],
    schemaName: 'Santa tracker and Christmas countdown',
  },


  hero: {
    badge: {
      preparing: 'North Pole workshop',
      liftoff: 'Final preparations',
      traveling: 'Delivery in progress',
      completed: 'Mission complete',
    },
    h1: {
      preparing: 'How long until Santa arrives?',
      liftoff: 'Santa is about to take off',
      traveling: 'Where is Santa right now?',
      completed: 'Santa is back at the North Pole',
    },
    intro: {
      preparing:
        'The North Pole never stops: elves are wrapping presents, the reindeer are training and the big list gets checked again and again. Here you can see how long until Christmas and how the preparations are going.',
      liftoff:
        "It's only hours until the longest journey of the year. The sleigh is loaded, Rudolph's nose is glowing and nobody at the North Pole is getting any sleep.",
      traveling:
        "It's Christmas Eve and the sleigh is circling the world. Follow the route on the map, see where Santa has just been and find out when he may reach your part of the world.",
      completed:
        'Every present has been delivered and the reindeer are resting at the North Pole. Next Christmas is already under way — here is the countdown.',
    },
    toMap: 'See the map',
  },

  countdown: {
    heading: {
      preparing: 'Santa reaches your area in',
      liftoff: 'The sleigh takes off in',
      traveling: 'He reaches your area in',
      completed: 'Next Christmas is in',
    },
    units: { days: 'days', hours: 'hours', minutes: 'minutes', seconds: 'seconds' },
    christmasNote: 'Christmas Day is 25 December: {days} days to go.',
    christmasToday: "It's Christmas today!",
    arrivingNow: "He's flying over your area!",
    ariaLabel: "Countdown to Santa's arrival",
  },

  status: {
    preparing: {
      far: [
        'The elves are making the first toys of the year.',
        'Santa has opened a brand new notebook for the big list.',
        'The workshop is testing gift ideas for next Christmas.',
        'The reindeer are slowly coming back from their holidays.',
      ],
      months: [
        'The North Pole workshop is working non-stop.',
        'Santa is going through his big list.',
        'The reindeer have started their training.',
        'The first letters from all over the world are arriving.',
      ],
      weeks: [
        'Mrs Claus is baking cookies for the whole workshop.',
        'Santa is checking the list for the second time.',
        'The elves are wrapping presents at full speed.',
        'The sleigh is in the workshop, ready for its service.',
      ],
      days: [
        "It's nearly time for the sleigh to take off!",
        'The elves are tying up the last sacks.',
        'Rudolph is practising for the big night.',
        'Nobody at the North Pole is sleeping any more.',
      ],
      hours: [
        'Santa is loading the last presents.',
        'The sleigh is ready for take-off.',
        'The reindeer are harnessed and waiting for the signal.',
        "Rudolph's nose is already glowing.",
      ],
    },
    liftoff: [
      'Santa is loading the last presents.',
      "Rudolph's nose is already glowing.",
      'The elves are tying up the big sack.',
      'The sleigh is ready for take-off.',
    ],
    traveling: [
      'The sleigh has just passed over {city}.',
      'Santa leaves {city} with a slightly lighter sack.',
      'Rudolph is leading the sleigh on from {city}.',
      '{city} has its presents already.',
    ],
    completed: [
      'Santa has finished his journey.',
      'Every present has been delivered.',
      'The reindeer are resting at the North Pole.',
      'Next Christmas is already being prepared.',
    ],
    lastVisited: 'Last place visited',
    nextStop: 'Next destination',
    flyingTo: 'Flying towards {leg}',
    atNorthPole: 'At the North Pole',
    backHome: 'Back home',
    legLabel: {
      'north-pole': 'the North Pole',
      pacific: 'the Pacific',
      oceania: 'Oceania',
      'east-asia': 'East Asia',
      'southeast-asia': 'South-East Asia',
      'central-asia': 'Central and South Asia',
      'middle-east': 'the Middle East',
      'eastern-europe': 'Eastern Europe',
      africa: 'Africa',
      'western-europe': 'Western Europe',
      'south-america': 'South America',
      'north-america': 'North America',
      'north-pole-return': 'the North Pole',
    },
  },

  map: {
    heading: "Santa's route",
    subheading: 'Tap the sleigh or any city to find out more.',
    ariaLabel: "World map showing Santa's route",
    sleighLabel: "Santa's sleigh",
    northPoleLabel: 'North Pole',
    legend: { visited: 'Already visited', current: 'He is here', upcoming: 'Not yet' },
    stopStatus: {
      visited: 'Santa has already been here',
      current: 'Santa is here right now',
      upcoming: 'Santa has not arrived yet',
    },
    localTime: 'Local time: {time}',
    close: 'Close',
    followSleigh: 'Follow the sleigh',
    wholeWorld: 'See the whole world',
    listHeading: 'The route, step by step',
    listIntro:
      'The same journey as a list, in case you would rather read it or the map is hard to see on your screen.',
    parentsNote:
      'A note for grown-ups: this is a simulated Christmas experience. The route is worked out from the world time zones — it is not a real location and there is no GPS tracking.',
  },

  eta: {
    heading: 'What time will he reach your house?',
    body: 'Santa should reach your area roughly between {start} and {end}.',
    zone: 'Worked out for your time zone ({zone}).',
    remaining: '{countdown} to go.',
    passed: 'Santa has already been through your area. Go and look under the tree!',
    unknownZone: "Worked out from your device's clock.",
    disclaimer:
      'It is an approximate window, not a guaranteed time: Santa prefers to arrive once everyone is asleep.',
  },

  stats: {
    heading: {
      preparing: 'Right now in the workshop',
      liftoff: 'All set for the big night',
      traveling: 'The journey in numbers',
      completed: 'How the journey went',
    },
    labels: {
      giftsReady: 'Presents ready',
      giftsDelivered: 'Presents delivered',
      lettersReceived: 'Letters received',
      elvesWorking: 'Elves working',
      reindeerTraining: 'Reindeer training',
      kilometers: 'Kilometres',
      citiesVisited: 'Cities visited',
      countriesVisited: 'Countries visited',
      homesWaiting: 'Homes waiting',
      travelMinutes: 'Time travelling',
    },
    units: { kilometers: 'km', hours: 'h' },
  },

  workshop: {
    heading: 'Right now at the North Pole',
    activities: {
      preparing: [
        'The elves are wrapping presents.',
        'Santa is reading letters.',
        'The reindeer are training on the runway.',
        'Mrs Claus is baking cookies.',
        'The sleigh is having its yearly service.',
        'Names are being double-checked in the big list room.',
      ],
      liftoff: [
        'The sleigh is having its final check.',
        'Rudolph is practising for the big night.',
        'The elves are loading the sacks onto the sleigh.',
        'Mrs Claus is making hot chocolate for the road.',
      ],
      traveling: [
        'The elves are following the journey from the control tower.',
        'Mrs Claus keeps the fire going, waiting for him to come home.',
        'The workshop is tidying away the last of the wrapping paper.',
        'The spare reindeer are standing by, just in case.',
      ],
      completed: [
        'The reindeer are resting in the North Pole stable.',
        'Mrs Claus is serving the after-journey hot chocolate.',
        'The elves are tidying the workshop for next year.',
        'Santa is putting the big list away until next Christmas.',
      ],
    },
    night: [
      'It is night at the North Pole: the workshop is working by lamplight.',
      'The night-shift elves are wrapping quietly.',
      'Santa is getting some sleep before the big day.',
      'All you can hear is the snow creaking around the workshop.',
    ],
  },

  surprises: {
    northPole: 'You woke up the northern lights! The elves are waving at you from the workshop.',
    celebration: 'Journey complete! Merry Christmas.',
  },

  controls: {
    soundOn: 'Turn on sound',
    soundOff: 'Mute',
    fullscreenEnter: 'Full screen',
    fullscreenExit: 'Exit full screen',
    share: 'Share',
    shareCopied: 'Link copied',
    motionOn: 'Turn animations on',
    motionOff: 'Reduce motion',
  },

  share: {
    title: 'Where is Santa?',
    text: {
      preparing: 'Find out how long until Santa arrives.',
      liftoff: 'Santa is about to take off!',
      traveling: 'See where Santa is right now!',
      completed: 'Santa has finished his journey. Merry Christmas!',
    },
  },

  faq: {
    heading: 'Frequently asked questions',
    items: [
      {
        question: 'How long until Christmas?',
        answer:
          'The countdown on this page tells you to the second. Christmas Day is 25 December, and Santa leaves the North Pole on the evening of the 24th.',
      },
      {
        question: 'Where is Santa now?',
        answer:
          'It depends on the day. Most of the year he is at the North Pole getting Christmas ready, and on Christmas Eve he travels the whole world. The map on this page shows where he should be at any given moment.',
      },
      {
        question: 'Which way does Santa go?',
        answer:
          'He starts in the Pacific, carries on through Oceania and Asia, crosses the Middle East, Europe and Africa, drops down to South America and finishes in North America before heading home. He is chasing the night: he delivers wherever it is already dark.',
      },
      {
        question: 'When does Santa leave the North Pole?',
        answer:
          'He takes off on the afternoon of 24 December, when it is almost midnight on the Pacific islands. From then on he does not stop until the morning of the 25th.',
      },
      {
        question: 'What time does Santa arrive?',
        answer:
          'He reaches each house once the children are asleep, roughly between nine in the evening and midnight. This page shows the approximate window worked out for your time zone.',
      },
      {
        question: 'Does Santa visit every country?',
        answer:
          'Yes — all 195 countries are on the route. The map marks a few cities as landmarks, but the sleigh goes everywhere.',
      },
      {
        question: 'How can he travel the world in a single night?',
        answer:
          'Because he flies west, chasing the night: going the same way the Earth turns stretches his Christmas Eve to more than thirty hours. That, and a little North Pole magic.',
      },
      {
        question: 'What happens if I am awake when he arrives?',
        answer:
          'Santa is very discreet: if he senses someone is still awake, he moves on and comes back later. The best way to get your presents early is an early night.',
      },
      {
        question: 'How does Santa know where I live?',
        answer:
          'From the letters. Every letter that reaches the North Pole is written into the big list with a name and a town, and the elves plan the route from there.',
      },
      {
        question: 'When does he get back to the North Pole?',
        answer:
          'On the morning of 25 December, after the last delivery. The reindeer rest for a few days and the workshop starts thinking about next Christmas.',
      },
      {
        question: 'Does the map show his real location?',
        answer:
          'No. This is a simulated Christmas experience: the route is worked out from the world time zones so that it matches night-time in each place. There is no GPS tracking and no real location, and this page never asks where you are.',
      },
      {
        question: 'How can I talk to Santa?',
        answer:
          'On PapaNoel.chat you can set up a call where Santa talks directly to your child, by name and in real time. You decide what he knows beforehand.',
      },
      {
        question: 'Can I have a video call with Santa?',
        answer:
          "Yes. As well as a voice call, you can set up a personalised video call where Santa knows your child's name, interests, achievements and Christmas wishes.",
      },
    ],
  },

  cta: {
    heading: 'While you wait for him, you can talk to Santa',
    body: "Set up a personalised video call where Santa will know your child's name, interests, achievements and Christmas wishes.",
    primary: 'Talk to Santa',
    secondary: 'Discover magical characters',
    free: 'Want to try first? Talk to Santa for free',
    travelingNote:
      'Santa is very busy tonight, but you can still set up a magical video call.',
  },

  stops: {
    'north-pole': 'North Pole',
    'north-pole-return': 'North Pole',
    auckland: 'Auckland',
    suva: 'Suva',
    sydney: 'Sydney',
    melbourne: 'Melbourne',
    brisbane: 'Brisbane',
    'port-moresby': 'Port Moresby',
    darwin: 'Darwin',
    tokyo: 'Tokyo',
    seoul: 'Seoul',
    shanghai: 'Shanghai',
    beijing: 'Beijing',
    'hong-kong': 'Hong Kong',
    manila: 'Manila',
    singapore: 'Singapore',
    jakarta: 'Jakarta',
    bangkok: 'Bangkok',
    dhaka: 'Dhaka',
    kathmandu: 'Kathmandu',
    'new-delhi': 'New Delhi',
    karachi: 'Karachi',
    kabul: 'Kabul',
    dubai: 'Dubai',
    tehran: 'Tehran',
    riyadh: 'Riyadh',
    istanbul: 'Istanbul',
    moscow: 'Moscow',
    athens: 'Athens',
    warsaw: 'Warsaw',
    cairo: 'Cairo',
    johannesburg: 'Johannesburg',
    lagos: 'Lagos',
    berlin: 'Berlin',
    rome: 'Rome',
    paris: 'Paris',
    madrid: 'Madrid',
    london: 'London',
    lisbon: 'Lisbon',
    reykjavik: 'Reykjavík',
    'rio-de-janeiro': 'Rio de Janeiro',
    'sao-paulo': 'São Paulo',
    'buenos-aires': 'Buenos Aires',
    santiago: 'Santiago',
    'la-paz': 'La Paz',
    caracas: 'Caracas',
    bogota: 'Bogotá',
    lima: 'Lima',
    'new-york': 'New York',
    toronto: 'Toronto',
    havana: 'Havana',
    'mexico-city': 'Mexico City',
    chicago: 'Chicago',
    denver: 'Denver',
    'los-angeles': 'Los Angeles',
    vancouver: 'Vancouver',
    anchorage: 'Anchorage',
    honolulu: 'Honolulu',
  },
}
